import { useEffect, useRef, useState } from "react";
import { Paperclip, ArrowUp, Image as ImageIcon, FileText, X } from "lucide-react";
import { toast } from "sonner";

const MAX_IMAGE = 8 * 1024 * 1024;
const MAX_PDF = 20 * 1024 * 1024;

const isImageFile = (f) =>
  !!f && (f.type?.startsWith("image/") || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(f.name || ""));

const isPdfFile = (f) =>
  !!f && (f.type === "application/pdf" || /\.pdf$/i.test(f.name || ""));

const ChatInput = ({ onSend, disabled }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null); // { file, type, name, previewUrl? }
  const imgRef = useRef(null);
  const pdfRef = useRef(null);

  // Cleanup object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
    };
  }, [file?.previewUrl]);

  const acceptImage = (f) => {
    if (!isImageFile(f)) {
      toast.error("Formato inválido. Use JPG, PNG, WebP ou GIF.");
      return;
    }
    if (f.size > MAX_IMAGE) {
      toast.error("Imagem maior que 8MB.");
      return;
    }
    if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
    setFile({
      file: f,
      type: "image",
      name: f.name || "imagem.png",
      previewUrl: URL.createObjectURL(f),
    });
  };

  const acceptPdf = (f) => {
    if (!isPdfFile(f)) {
      toast.error("Selecione um PDF válido.");
      return;
    }
    if (f.size > MAX_PDF) {
      toast.error("PDF maior que 20MB.");
      return;
    }
    if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
    setFile({ file: f, type: "pdf", name: f.name || "documento.pdf" });
  };

  const handlePickImage = (e) => {
    const f = e.target.files?.[0];
    if (f) acceptImage(f);
    e.target.value = "";
  };

  const handlePickPdf = (e) => {
    const f = e.target.files?.[0];
    if (f) acceptPdf(f);
    e.target.value = "";
  };

  // Paste support — when user pastes an image from clipboard
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === "file" && it.type?.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) {
          e.preventDefault();
          // Some pastes produce nameless files — give them one
          if (!f.name) {
            const ext = (f.type.split("/")[1] || "png").toLowerCase();
            try {
              Object.defineProperty(f, "name", { value: `colado-${Date.now()}.${ext}` });
            } catch (_) {}
          }
          acceptImage(f);
          return;
        }
      }
    }
    // Also try files API directly (some browsers)
    const f = e.clipboardData?.files?.[0];
    if (f && f.type?.startsWith("image/")) {
      e.preventDefault();
      acceptImage(f);
    }
  };

  const removeFile = () => {
    if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
    setFile(null);
  };

  const submit = () => {
    const message = text.trim();
    if (!message && !file) return;
    if (disabled) return;
    onSend(message || (file ? `Analise este ${file.type === "pdf" ? "documento" : "material"}.` : ""), file?.file);
    setText("");
    if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
    setFile(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#3A3A3A] via-[#3A3A3A]/95 to-transparent pb-8 pt-12 px-4 z-10" data-testid="chat-input-wrapper">
      <div className="max-w-3xl mx-auto">
        {file && (
          <div
            className="mb-3 inline-flex items-center gap-3 bg-[#4A4A4A] border border-[#C9A84C]/40 rounded-2xl pl-2 pr-3 py-2 text-[12px] text-zinc-100"
            data-testid="attached-file-chip"
          >
            {file.type === "image" && file.previewUrl ? (
              <img
                src={file.previewUrl}
                alt={file.name}
                className="w-12 h-12 rounded-lg object-cover border border-[#5a5a5a]"
                data-testid="attached-image-preview"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-[#3a3a3a] border border-[#5a5a5a] flex items-center justify-center">
                <FileText size={20} strokeWidth={1.4} className="text-[#C9A84C]" />
              </div>
            )}
            <div className="flex flex-col min-w-0">
              <span className="truncate max-w-[260px] text-[13px] text-zinc-100">{file.name}</span>
              <span className="text-[10.5px] text-zinc-400 uppercase tracking-[0.15em]">
                {file.type === "image" ? "Imagem · Pronta para envio" : "PDF · Pronto para envio"}
              </span>
            </div>
            <button
              onClick={removeFile}
              className="ml-2 text-zinc-400 hover:text-white p-1 rounded hover:bg-white/5"
              data-testid="remove-attached-file"
              type="button"
              aria-label="Remover arquivo"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div
          className="stoneia-input-shell bg-[#424242] rounded-3xl shadow-[0_0_60px_-20px_rgba(0,0,0,0.9)] flex items-end gap-2 p-2 transition-all"
        >
          <input
            ref={imgRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/*"
            hidden
            onChange={handlePickImage}
            data-testid="image-input"
          />
          <input
            ref={pdfRef}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={handlePickPdf}
            data-testid="pdf-input"
          />
          <div className="flex items-center gap-1 pl-2 pb-2">
            <button
              type="button"
              onClick={() => imgRef.current?.click()}
              disabled={disabled}
              data-testid="upload-image-btn"
              className="text-zinc-400 hover:text-[#C9A84C] p-1.5 rounded-md hover:bg-white/5 transition disabled:opacity-30"
              title="Enviar imagem (também aceita colar com Ctrl+V)"
            >
              <ImageIcon size={16} strokeWidth={1.6} />
            </button>
            <button
              type="button"
              onClick={() => pdfRef.current?.click()}
              disabled={disabled}
              data-testid="upload-pdf-btn"
              className="text-zinc-400 hover:text-[#C9A84C] p-1.5 rounded-md hover:bg-white/5 transition disabled:opacity-30"
              title="Enviar PDF"
            >
              <Paperclip size={16} strokeWidth={1.6} />
            </button>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={handlePaste}
            disabled={disabled}
            placeholder="Pergunte à StoneIA — você também pode colar uma imagem com Ctrl+V"
            rows={1}
            data-testid="chat-textarea"
            className="flex-1 resize-none bg-transparent outline-none text-[15px] leading-[1.5] py-3 max-h-40 font-light placeholder:text-[#888888]"
            style={{ fontFamily: "Manrope, sans-serif", color: "#F0F0F0" }}
          />
          <button
            type="button"
            onClick={submit}
            disabled={disabled || (!text.trim() && !file)}
            data-testid="send-btn"
            className="m-1 w-9 h-9 rounded-full flex items-center justify-center transition disabled:opacity-30 disabled:hover:!bg-[#C9A84C]"
            style={{ backgroundColor: "#C9A84C", color: "#0A0A0A" }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#D9BC6A"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#C9A84C"; }}
          >
            <ArrowUp size={16} strokeWidth={2} />
          </button>
        </div>
        <div className="text-center text-[10.5px] text-zinc-500 mt-3 tracking-wide">
          A StoneIA responde apenas sobre pedras decorativas e superfícies minerais.
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
